import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '../../../components/ui/Icon';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Breadcrumb } from '../../../components/ui/Breadcrumb';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { PageActions } from '../../../components/layout/PageActions';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useUrlState } from '../../../hooks/useUrlState';
import { ADMIN_HOME } from '../../../lib/adminRoutes';
import { adminOverviewQuery, qontoStatusQuery } from '../adminApi';
import { useAdminActions } from '../useAdminActions';

/**
 * I pezzi comuni alle sei pagine dell'Amministrazione (Situazione, Fatture,
 * Spese, Fisco, Dipendenti, Banca). Ogni pagina resta un file suo nella sua
 * cartella; qui sta solo quello che deve essere uguale dappertutto:
 *
 *   AdminHeader    titolo, sottotitolo, breadcrumb delle sottopagine, comandi
 *   ViewTabs       le linguette di vista (`?vista=`), come in Progetti
 *   useVista       lo stato della vista: cambiarla azzera ricerca e filtri
 *   useStorico     il filtro "Anche prima del 2026" (`?storico=1`)
 *   useSyncAll     "Aggiorna banca e fatture": Qonto e FattureInCloud insieme
 *   InCostruzione  lo stato vuoto delle viste che arrivano con i prossimi lotti
 */

// ════════════════════════════════════════════════════════════════════
// Intestazione
// ════════════════════════════════════════════════════════════════════

/**
 * Sul computer: titolo a sinistra, comandi a destra (come Contatti, Progetti,
 * Trattative). Sul telefono il titolo lo dice la barra in alto (mobile.md
 * 4bis), i comandi rari salgono lassù con PageActions dentro il menu ⋯, e
 * l'azione principale scende nella `.m-actionbar` della pagina.
 *
 * Il breadcrumb sta DENTRO la prima colonna dell'intestazione e non sopra:
 * così `.page-ed-hd` resta il primo figlio della pagina e sul telefono vale la
 * regola delle pagine di elenco (titolo ritagliato, niente prosa). Sul computer
 * si vede identico a quello di Pipeline, sopra il titolo.
 */
export function AdminHeader({ title, sub, crumb = true, actions, menu = [], primary }: {
  title: string;
  sub?: ReactNode;
  /** Breadcrumb "Amministrazione › Pagina": in tutte le sottopagine, non nella Situazione. */
  crumb?: boolean;
  /** Comandi secondari del computer (Aggiorna banca e fatture, Anche gli anni prima del 2026). */
  actions?: ReactNode;
  /** Gli stessi comandi, sul telefono: voci del menu ⋯ nella barra in alto. */
  menu?: ActionItem[];
  /** L'azione principale della pagina, sul computer. Sul telefono la mette la pagina in basso. */
  primary?: ReactNode;
}) {
  const isMobile = useIsMobile();
  return (
    <div className="page-ed-hd flex items-end justify-between gap-4">
      <div className="min-w-0">
        {crumb && <Breadcrumb items={[{ label: 'Amministrazione', to: ADMIN_HOME }, { label: title }]} />}
        <h1 className="ed-title">{title}</h1>
        {sub && <div className="ed-sub">{sub}</div>}
      </div>
      <PageActions className="flex items-center gap-2 pb-1 adm-head-actions">
        {isMobile
          ? menu.length > 0 && <ActionsMenu variant="ghost" label="Azioni" items={menu} />
          : <>{actions}{primary}</>}
      </PageActions>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Viste
// ════════════════════════════════════════════════════════════════════

/** Parametri che sopravvivono al cambio di vista: il filtro dello storico è della pagina, non della vista. */
const KEEP_ON_VIEW_CHANGE = ['storico'];

/**
 * La vista corrente di una pagina (`?vista=`, la prima è la predefinita e non
 * compare nell'URL). Cambiare vista riparte da una lista pulita: via la
 * ricerca `q` e i filtri della vista di prima (status, type, per, acc…), che
 * prima passavano da una tab all'altra (mappa § 7, incoerenza 4).
 */
export function useVista<T extends string>(keys: readonly T[]): [T, (next: T) => void] {
  const [raw] = useUrlState<string>('vista', keys[0]!);
  const [, setParams] = useSearchParams();
  const vista = ((keys as readonly string[]).includes(raw) ? raw : keys[0]) as T;
  const setVista = useCallback((next: T) => {
    setParams((prev) => {
      const sp = new URLSearchParams();
      for (const k of KEEP_ON_VIEW_CHANGE) { const v = prev.get(k); if (v) sp.set(k, v); }
      if (next !== keys[0]) sp.set('vista', next);
      return sp;
    });
  }, [keys, setParams]);
  return [vista, setVista];
}

export interface ViewTab<T extends string> {
  key: T;
  label: string;
  /** Pallino con un conteggio (mai un importo): solo se > 0. */
  count?: number | null;
  title?: string;
}

/**
 * Le linguette. Sul telefono scorrono di lato (mobile.css): la linguetta
 * accesa si porta in vista da sola, all'apertura e a ogni cambio, altrimenti
 * su "Altri versamenti al fisco" (l'ultima di sei) non si vedeva quale fosse
 * accesa. Scorre solo la fila, mai la pagina: niente scrollIntoView, che
 * muoverebbe anche la pagina in verticale.
 */
export function ViewTabs<T extends string>({ tabs, active, onChange }: { tabs: ViewTab<T>[]; active: T; onChange: (k: T) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const primaVolta = useRef(true);
  useEffect(() => {
    const fila = ref.current;
    const accesa = fila?.querySelector<HTMLElement>('.page-tab[aria-selected="true"]');
    if (!fila || !accesa || fila.scrollWidth <= fila.clientWidth) { primaVolta.current = false; return; }
    const f = fila.getBoundingClientRect();
    const a = accesa.getBoundingClientRect();
    // Già tutta in vista (con un po' d'aria ai lati): non si muove niente.
    const aria = 16;
    if (a.left >= f.left + aria && a.right <= f.right - aria) { primaVolta.current = false; return; }
    const centro = a.left + a.width / 2 - (f.left + f.width / 2);
    fila.scrollTo({ left: fila.scrollLeft + centro, behavior: primaVolta.current ? 'auto' : 'smooth' });
    primaVolta.current = false;
  }, [active]);

  return (
    <div className="page-tabs" ref={ref} role="tablist">
      {tabs.map((t) => (
        <div key={t.key} className="page-tab" role="tab" tabIndex={0} aria-selected={active === t.key} title={t.title}
          onClick={() => onChange(t.key)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(t.key); } }}>
          {t.label}
          {t.count != null && t.count > 0 && <span className="c">{t.count}</span>}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// "Anche prima del 2026"
// ════════════════════════════════════════════════════════════════════

/**
 * Lo storico: di default le liste partono dalla data di taglio
 * (OrgSettings.adminCutoffDate, 1/1/2026). Niente è cancellato: questo filtro
 * rimette in lista anche gli anni prima. Stesso meccanismo del vecchio
 * interruttore "Anni scorsi" (`storico=1` → `includeHistory=true`), ma vale
 * solo nelle liste che lo usano: Fatture, Spese e Banca › Movimenti.
 */
export function useStorico() {
  const [raw, set] = useUrlState<string>('storico', '');
  const { data: ov } = useQuery(adminOverviewQuery);
  const year = ov?.history?.cutoffDate?.slice(0, 4) ?? '2026';
  const on = raw === '1';
  return {
    on,
    year,
    set: (v: boolean) => set(v ? '1' : ''),
    /** Il bottone del computer. */
    button: (
      <button
        className={on ? 'btn btn-sm is-active' : 'btn btn-sm btn-ghost'}
        aria-pressed={on}
        title={on ? `Torna a guardare solo dal ${year}` : `Rimetti in lista anche quello che è prima del ${year}`}
        onClick={() => set(on ? '' : '1')}
      >
        <Icon name="clock" size={10} /> Anche gli anni prima del {year}
      </button>
    ),
    /** La voce del menu ⋯ del telefono. */
    menuItem: {
      key: 'storico',
      label: on ? `Solo dal ${year} in poi` : `Anche gli anni prima del ${year}`,
      icon: 'clock',
      onClick: () => set(on ? '' : '1'),
    } satisfies ActionItem,
  };
}

/** La riga che ricorda che si sta guardando anche lo storico, con il modo di uscirne. */
export function StoricoNote({ year, onOff }: { year: string; onOff: () => void }) {
  return (
    <div className="adm-note">
      <Icon name="clock" size={12} className="mute" />
      <span>
        In lista c'è <strong>anche quello che è prima del {year}</strong>.{' '}
        <a className="underline cursor-pointer" onClick={onOff}>Torna a guardare solo dal {year} in poi</a>
      </span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// "Aggiorna banca e fatture"
// ════════════════════════════════════════════════════════════════════

/**
 * Le due sincronizzazioni che stavano nel menu "Azioni": banca (Qonto) e
 * fatture (FattureInCloud), lanciate insieme. Qonto solo se è collegato.
 * Ognuna chiude col suo messaggio, come prima.
 */
export function useSyncAll() {
  const { syncQonto, syncFic } = useAdminActions();
  const { data: qonto } = useQuery(qontoStatusQuery);
  const { data: ov } = useQuery(adminOverviewQuery);
  const qontoConnected = qonto?.connected ?? ov?.sync?.qonto?.connected ?? false;
  const pending = syncQonto.isPending || syncFic.isPending;
  const run = () => {
    if (qontoConnected) syncQonto.mutate();
    syncFic.mutate();
  };
  return {
    pending,
    run,
    button: (
      <button className="btn" disabled={pending} onClick={run} title="Scarica i movimenti da Qonto e le fatture da FattureInCloud">
        <Icon name="refresh" size={12} /> {pending ? 'Aggiornamento in corso…' : 'Aggiorna banca e fatture'}
      </button>
    ),
    menuItem: {
      key: 'sync',
      label: pending ? 'Aggiornamento in corso…' : 'Aggiorna banca e fatture',
      icon: 'refresh',
      disabled: pending,
      onClick: run,
    } satisfies ActionItem,
  };
}

// ════════════════════════════════════════════════════════════════════
// Viste non ancora costruite
// ════════════════════════════════════════════════════════════════════

/** Stato vuoto sobrio: dice cosa ci sarà e, se c'è, dove si trovano i dati nel frattempo. */
export function InCostruzione({ descrizione, action }: { descrizione: ReactNode; action?: ReactNode }) {
  return <EmptyState icon="clock" title="In costruzione" description={descrizione} action={action} />;
}
